import React, {
  useState,
  ChangeEvent,
  FormEvent,
  useCallback,
  useEffect,
  useRef,
} from "react";
import {
  Checkbox,
  FormControl,
  FormControlLabel,
  RadioGroup,
  Radio,
  Select,
  MenuItem,
  Button,
  Box,
  Typography,
  InputLabel,
  SelectChangeEvent,
  FormLabel,
  OutlinedInput,
  Tooltip,
  TextField,
  InputAdornment,
  Switch,
  IconButton,
} from "@mui/material";
import { DatePicker, TimePicker } from "@mui/x-date-pickers";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import {
  IField,
  IDynamicFormFieldsJson,
  IFormValues,
  ISection,
  IOption,
  IAsyncOptionFn,
  IAsyncValueFn,
  IAsyncLabelFn,
  IGetOptionsResponse,
} from "../../models/user/DynamicForm/DynamicForm";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import { styled } from "@mui/material/styles";
import dayjs from "dayjs";
import FileAClaimStyles from "../../components/FileAClaim/FileAClaimStyles";
import tickMark from "../../assets/Grid.svg";
import dotImage from "../../assets/dot.svg";
import document from "../../assets/fileDocument.svg";
import trashBin from "../../assets/trash-2.svg";
import { getFileSizeWithUnits } from "../../utils/Utility";
import { useTranslation } from "react-i18next";
import { CustomAction } from "../../store/customAction";
import { Api } from "../..";
import { API_GET_LOOKUP_VALUES } from "../../Apis/ApiEndPoints";
import { useAppDispatch } from "../../store/hooks";
import FormFieldVoiceMic from "../VoiceInput/FormFieldVoiceMic";

/**
 * DynamicForm component props
 * 
 * @fieldDependencies - Optional configuration for field dependencies
 * Example usage:
 * ```typescript
 * const fieldDependencies = {
 *   fieldA: {
 *     dependencies: ["fieldB", "fieldC"],
 *     logic: (changedField, value, allFields) => {
 *       const fieldB = allFields.find(f => f.name === "fieldB");
 *       const fieldC = allFields.find(f => f.name === "fieldC");
 *       
 *       if (fieldB && fieldC) {
 *         // Custom logic here
 *         fieldB.disabled = value === "someValue";
 *         fieldC.value = value === "someValue" ? "default" : "";
 *       }
 *     }
 *   }
 * };
 * ```
 */
interface IProps {
  formFieldsJson: IDynamicFormFieldsJson | null;
  asyncOptionsFunctions?: { [key: string]: IAsyncOptionFn };
  asyncValueFunctions?: { [key: string]: IAsyncValueFn };
  asyncLabelFunctions?: { [key: string]: IAsyncLabelFn };
  onFormSubmit: (formValues: IFormValues) => void;
  onFieldChange?: (fieldName: string, value: any) => void;
  isUpdate?: boolean;
  isFileAClaim?: string;
  fieldDependencies?: {
    [fieldName: string]: {
      dependencies: string[];
      logic: (changedField: string, value: any, allFields: IField[]) => void;
    };
  };
  rightActions?: React.ReactNode;
}

const VisuallyHiddenInput = styled("input")({
  clip: "rect(0 0 0 0)",
  clipPath: "inset(50%)",
  height: 1,
  overflow: "hidden",
  position: "absolute",
  bottom: 0,
  left: 0,
  whiteSpace: "nowrap",
  width: 1,
});

const DynamicForm: React.FC<IProps> = ({
  formFieldsJson,
  asyncOptionsFunctions,
  asyncValueFunctions,
  asyncLabelFunctions,
  isUpdate,
  isFileAClaim,
  onFormSubmit,
  onFieldChange,
  fieldDependencies,
  rightActions,
}) => {
  const ITEM_HEIGHT = 48;
  const ITEM_PADDING_TOP = 8;

  const dispatch = useAppDispatch();
  const [formSchema, setFormSchema] = useState<IDynamicFormFieldsJson>({});

  const fileInputRefs = useRef<{ [key: string]: HTMLInputElement | null }>({});
  const cursorPositionRef = useRef<number | null>(null);
  // Voice input is handled by VoiceMic component

  const { t } = useTranslation();

  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const isMobile = windowWidth <= 1023;

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const fetchValueSelectOptions = useCallback(async (fieldName: string) => {
    try {
      const response: IGetOptionsResponse = await CustomAction(
        Api.get(`${API_GET_LOOKUP_VALUES}/${fieldName}`),
        dispatch,
        "getVehicleUsageOptions"
      );
      return response.data;
    } catch (error) {
      return [];
    }
  }, []);

  // Voice helpers
  const VOICE_FIELDS = useRef(new Set<string>([
    "incidentLocation",
    "incidentDesc",
    "incidentDescription",
    "description",
    "lossLocation",
    "lossAddress",
    "incidentAddr",
  ]));
  const isVoiceField = useCallback((name?: string, label?: string) => {
    if (name && VOICE_FIELDS.current.has(name)) return true;
    const lbl = (label || "").toString().toLowerCase();
    if (!lbl) return false;
    return (
      lbl.includes("incident location") ||
      lbl.includes("incident description") ||
      lbl.includes("description") ||
      lbl.includes("loss location")
    );
  }, []);

  // Live update a single field's value without running full validation/dependency cycle
  const setFieldValueLive = useCallback((fieldName: string, value: string) => {
    setFormSchema((prev) => {
      const newSections = (prev.sections || []).map((section) => ({
        ...section,
        fields: section.fields.map((f) => (f.name === fieldName ? { ...f, value } : f)),
      }));
      return { ...prev, sections: newSections };
    });
  }, []);

  

  const handleInputChange = useCallback(
    async (
      name: string,
      value: any,
      type?: string,
      disableFuture?: boolean,
      disablePast?: boolean
    ) => {
      const newSections: ISection[] = await Promise.all(
        (formSchema.sections || []).map(async (section) => {
          const updatedFields: IField[] = await Promise.all(
            section.fields.map(async (field) => {
              let newField = { ...field };
              // Only clear the paired time field when its date changes
              if (
                name === "Date" &&
                newField.name === "Time"
                // Add more pairs here if needed
              ) {
                newField.value = "";
                newField.error = "";
              }
              if (newField.name === name) {
                if (type === "file") {
                  newField.value = value;
                }
                // Always store time fields as dayjs objects
                if (type === "time") {
                  newField.value = value
                    ? dayjs.isDayjs(value)
                      ? value
                      : dayjs(value)
                    : null;
                }
                // Always store year fields as dayjs objects
                if (type === "year") {
                  newField.value = value
                    ? dayjs.isDayjs(value)
                      ? value
                      : dayjs(value)
                    : null;
                }
                if (
                  type === "date" &&
                  disableFuture &&
                  value &&
                  value.isAfter(dayjs())
                ) {
                  newField.error = "Future dates are not allowed";
                }
                else if (
                  type === "year" &&
                  value &&
                  (dayjs.isDayjs(value) ? value.year() : dayjs(value).year()) > dayjs().year()
                ) {
                  newField.error = "Future year are not allowed";
                }
                else if (type === "year" && value) {
                  const yearFormatted = dayjs.isDayjs(value)
                    ? value.format("YYYY")
                    : dayjs(value).format("YYYY");
                    const isFourDigitsNoLeadingZero = /^[1-9][0-9]{3}$/.test(yearFormatted);
                    const yearNumber = parseInt(yearFormatted, 10);
                   if (!isFourDigitsNoLeadingZero || yearNumber < 1900) {
                   newField.error = "Invalid year";
                  } else {
                     newField.error = "";
                    }
                }
                else if (
                  type === "date" &&
                  value &&
                  value.isBefore(dayjs().startOf("day")) &&
                  disablePast &&
                  value.isBefore(dayjs())
                ) {
                  newField.error = "Past dates are not allowed";
                } else if (type === "time" && disableFuture && value) {
                  // Pair the time field with its corresponding date field by name convention
                  // Example: lossTime -> lossDate, reportedTime -> reportedDate
                  const pairedDateFieldName =
                    newField.name.endsWith("Time")
                      ? newField.name.replace(/Time$/, "Date")
                      : undefined;

                  const allFields = formSchema.sections
                    ?.flatMap((section) => section.fields) || [];

                  const pairedDateField = pairedDateFieldName
                    ? allFields.find(
                        (f) => f.type === "date" && f.name === pairedDateFieldName
                      )
                    : undefined;

                  // Fallback to first available date if no explicit pair is found
                  const selectedDateField =
                    pairedDateField || allFields.find((f) => f.type === "date" && f.value);

                  const selectedDate = selectedDateField?.value
                    ? dayjs(selectedDateField.value)
                    : null;

                  const today = dayjs();

                  if (selectedDate && selectedDate.isSame(today, "day")) {
                    const currentTime = today.hour() * 60 + today.minute();
                    const selectedTime = value.hour() * 60 + value.minute();
                    if (selectedTime > currentTime) {
                      newField.error = t(
                        "Future time is not allowed for today's date"
                      );
                    } else {
                      newField.error = "";
                      newField.value = value;
                    }
                  } else {
                    newField.error = "";
                    newField.value = value;
                  }
                } else {
                  // Always set the value first, then validate
                  newField.value = value;
                  
                  if (
                    newField.length &&
                    typeof value === "string" &&
                    value.length > newField.length
                  ) {
                    newField.error = `${t("Maximum length of")} ${
                      newField.length
                    } ${t("characters exceeded")}`;
                  } else if (
                    newField.minLength &&
                    typeof value === "string" &&
                    value.length < newField.minLength &&
                    value.length > 0
                  ) {
                    newField.error = `${t("Minimum length of")} ${
                      newField.minLength
                    } ${t("characters required")}`;
                  } else {
                    newField.error = "";
                  }
                }
              }
              if (
                newField.asyncType === "asyncValue" &&
                newField.asyncEventType === "onChange" &&
                newField.asyncValueFn &&
                newField.asyncSourceName === name
              ) {
                newField.error = "";
                newField.value = await newField.asyncValueFn(value);
              }
              if (
                newField.asyncType === "asyncOptions" &&
                newField.asyncEventType === "onChange" &&
                newField.asyncOptionsFn &&
                newField.asyncSourceName === name
              ) {
                newField.error = "";
                newField.options = await newField.asyncOptionsFn(value);
              }
              let flags: string[] = [];
              if (
                newField.condition &&
                newField.condition.conditionOptions &&
                newField.condition.conditionOptions.length > 0
              ) {
                newField.error = "";
                newField.condition.conditionOptions.forEach(
                  (conditionOption) => {
                    if (conditionOption.source === name) {
                      if (conditionOption.value === value) {
                        flags.push("true");
                      } else {
                        flags.push("false");
                      }
                      if (conditionOption.attribute === "hidden") {
                        newField.hidden = !flags.includes("true");
                        // Clear value when field becomes hidden to avoid submitting stale values
                        if (newField.hidden) {
                          newField.value = "";
                          newField.error = "";
                        }
                      } else if (conditionOption.attribute === "disable") {
                        newField.disabled = flags.includes("true");
                      } else if (conditionOption.attribute === "required") {
                        newField.required = flags.includes("true");
                      }
                    }
                  }
                );
              }
              return newField;
            })
          );
          return {
            ...section,
            fields: updatedFields,
          };
        })
      );
      
      const updatedSchema = { ...formSchema, sections: newSections };
      const allFields = updatedSchema.sections?.flatMap(s => s.fields) || [];
      
      // Handle generic field dependencies
      if (fieldDependencies && fieldDependencies[name]) {
        const dependencyConfig = fieldDependencies[name];
        dependencyConfig.logic(name, value, allFields);
      }
      
      // Update the schema with any changes made by field dependencies
      const updatedSections = updatedSchema.sections?.map(section => ({
        ...section,
        fields: section.fields.map(field => {
          const updatedField = allFields.find(f => f.name === field.name);
          return updatedField || field;
        })
      }));
      
      if (updatedSections) {
        updatedSchema.sections = updatedSections;
      }
      
      // Call onFieldChange for all field changes to allow parent component to handle custom logic
      if (onFieldChange) {
        onFieldChange(name, value);
      }
      
      setFormSchema(updatedSchema);
    },
    [formSchema]
  );

  // (removed: speech recognition handlers; delegated to VoiceMic)

  const handleFileDelete = useCallback((fieldName: string) => {
    setFormSchema((prevState) => ({
      ...prevState,
      sections: prevState.sections?.map((section) => ({
        ...section,
        fields: section.fields.map((field) => {
          const updatedField = { ...field };
          if (field.name === fieldName) {
            updatedField.value = "";
            return updatedField;
          }
          return updatedField;
        }),
      })),
    }));
    if (fileInputRefs.current[fieldName]) {
      fileInputRefs.current[fieldName]!.value = "";
    }
  }, []);

  // Create a separate component for valueselect
  const ValueSelectField: React.FC<{
    field: IField;
    handleInputChange: (name: string, value: any) => void;
    t: (key: string) => string;
  }> = ({ field, handleInputChange, t }) => {
    const [options, setOptions] = useState<IOption[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
      const fetchOptions = async () => {
        const fetchedOptions = await fetchValueSelectOptions(field.name);
        setOptions(fetchedOptions || []); // Default to an empty array if fetchedOptions is undefined
        setLoading(false);
      };
      fetchOptions();
    }, [field.name]);

    if (loading) {
      return <Typography>Fetching values...</Typography>;
    }

    return (
      <>
        <InputLabel>{`${t(field.label)}${
          field.required ? "*" : ""
        }`}</InputLabel>
        <Select
          value={field.value}
          onChange={(e: SelectChangeEvent) =>
            handleInputChange(field.name, e.target.value as string)
          }
          label={field.label}
          placeholder={field.placeholder}
          disabled={field.disabled}
          MenuProps={{
            anchorOrigin: {
              vertical: "bottom",
              horizontal: "left",
            },
            transformOrigin: {
              vertical: "top",
              horizontal: "left",
            },
            PaperProps: {
              style: {
                maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
                overflow: "auto",
              },
            },
          }}
        >
          {options?.map((option) => (
            <MenuItem key={option.code} value={option.code}>
              {option.label}
            </MenuItem>
          ))}
        </Select>
      </>
    );
  };

  const renderInputField = useCallback(
    (field: IField): JSX.Element | null => {
      const fieldComponent: { [key: string]: () => JSX.Element } = {
        text: () => (
          <>
            <InputLabel>{`${t(field.label)}${
              field.required ? "*" : ""
            }`}</InputLabel>
            <OutlinedInput
              id={`${field.label}-text`}
              data-test-id={`test-${field.label}`}
              aria-label={`${field.label}`}
              type="text"
              label={t(field.label)}
              value={field.value || ""}
              endAdornment={
                isVoiceField(field.name, field.label) ? (
                  <InputAdornment position="end">
                    <FormFieldVoiceMic
                      fieldName={field.name}
                      baseValue={(field.value || "") as string}
                      disabled={Boolean(field.disabled)}
                      onLiveChange={(v) => setFieldValueLive(field.name, v)}
                      onFinalize={(v) => handleInputChange(field.name, v)}
                    />
                  </InputAdornment>
                ) : undefined
              }
              onSelect={(e: React.SyntheticEvent<HTMLInputElement>) => {
                cursorPositionRef.current = e.currentTarget.selectionStart;
              }}
              onChange={(e: ChangeEvent<HTMLInputElement>) => {
                const input = e.target;
                const newValue = input.value;
                cursorPositionRef.current = input.selectionStart;

                handleInputChange(field.name, newValue).then(() => {
                  if (cursorPositionRef.current !== null) {
                    input.setSelectionRange(
                      cursorPositionRef.current,
                      cursorPositionRef.current
                    );
                  }
                });
              }}
              placeholder={field.placeholder}
              disabled={field.disabled}
            />
          </>
        ),
        textarea: () => (
          <>
            <InputLabel>{`${t(field.label)}${
              field.required ? "*" : ""
            }`}</InputLabel>
            <OutlinedInput
              id={`${field.label}-text-area`}
              data-test-id={`test-${field.label}`}
              aria-label={`${field.label}`}
              sx={{ minHeight: "150px", alignItems: "start" }}
              label={t(field.label)}
              value={field.value || ""}
              multiline
              maxRows={field.maxRows}
              rows={field.rows}
              inputProps={{ maxLength: field.length }}
              endAdornment={
                isVoiceField(field.name, field.label) ? (
                  <InputAdornment position="end">
                    <FormFieldVoiceMic
                      fieldName={field.name}
                      baseValue={(field.value || "") as string}
                      disabled={Boolean(field.disabled)}
                      onLiveChange={(v) => setFieldValueLive(field.name, v)}
                      onFinalize={(v) => handleInputChange(field.name, v)}
                    />
                  </InputAdornment>
                ) : undefined
              }
              onSelect={(e: React.SyntheticEvent<HTMLInputElement>) => {
                cursorPositionRef.current = e.currentTarget.selectionStart;
              }}
              onChange={(e: ChangeEvent<HTMLInputElement>) => {
                const input = e.target;
                const newValue = input.value;
                const limitedValue =
                  typeof field.length === "number" && field.length > 0
                    ? newValue.slice(0, field.length)
                    : newValue;
                cursorPositionRef.current = input.selectionStart;

                handleInputChange(field.name, limitedValue).then(() => {
                  if (cursorPositionRef.current !== null) {
                    input.setSelectionRange(
                      cursorPositionRef.current,
                      cursorPositionRef.current
                    );
                  }
                });
              }}
              placeholder={field.placeholder}
              disabled={field.disabled}
            />
            {/* Character counter for textarea fields */}
            {(field.length || field.minLength) && (
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                <Typography 
                  variant="caption" 
                  color={field.value && field.length && field.value.length > field.length ? "error" : "textSecondary"}
                >
                  {field.value ? field.value.length : 0}
                  {field.length && ` / ${field.length}`}
                  {field.minLength && field.value && field.value.length < field.minLength && 
                    ` (min: ${field.minLength})`
                  }
                </Typography>
              </Box>
            )}
          </>
        ),
        number: () => (
          <>
            <InputLabel>{`${t(field.label)}${
              field.required ? "*" : ""
            }`}</InputLabel>
            {(
              <OutlinedInput
                id={`${field.label}-number`}
                data-test-id={`test-${field.label}`}
                aria-label={`${field.label}`}
                type="text"
                label={t(field.label)}
                value={typeof field.value === "string" ? field.value : field.value ?? ""}
                onChange={(e: ChangeEvent<HTMLInputElement>) => {
                  const rawValue = e.target.value;
                  let digitsOnly = rawValue.replace(/\D/g, "");
                  if (field.length && digitsOnly.length > field.length) {
                    digitsOnly = digitsOnly.slice(0, field.length);
                  }
                  handleInputChange(field.name, digitsOnly);
                }}
                inputProps={{
                  inputMode: "numeric",
                  pattern: "[0-9]*",
                  maxLength: (field.length as number | undefined),
                }}
                placeholder={field.placeholder}
                disabled={field.disabled}
              />
            )}
          </>
        ),
        checkbox: () => (
          <FormControlLabel
            label={`${t(field.label)}${field.required ? "*" : ""}`}
            control={
              <Checkbox
                id={`${field.label}-Checkbox`}
                data-test-id={`test-${field.label}`}
                aria-label={`${field.label}`}
                checked={Boolean(field.value)}
                onChange={(e: ChangeEvent<HTMLInputElement>) =>
                  handleInputChange(field.name, e.target.checked)
                }
              />
            }
          />
        ),
        radio: () => (
          <>
            <FormLabel>{`${t(field.label)}${
              field.required ? "*" : ""
            }`}</FormLabel>
            <RadioGroup
              id={`${field.label}-Radio`}
              data-test-id={`test-${field.label}`}
              aria-label={`${field.label}`}
              row
              value={field.value}
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                handleInputChange(field.name, e.target.value)
              }
            >
              {field.options?.map((option: IOption) => (
                <FormControlLabel
                  key={option.code}
                  value={option.code}
                  control={<Radio />}
                  label={option.label}
                />
              ))}
            </RadioGroup>
          </>
        ),
        valueselect: () => (
          <ValueSelectField
            field={field}
            handleInputChange={handleInputChange}
            t={t}
          />
        ),
        select: () => (
          <>
            <InputLabel>{`${t(field.label)}${
              field.required ? "*" : ""
            }`}</InputLabel>
            <Select
              id={`${field.label}-Select`}
              data-test-id={`test-${field.label}`}
              aria-label={`${field.label}`}
              value={field.value}
              onChange={(e: SelectChangeEvent) =>
                handleInputChange(field.name, e.target.value as string)
              }
              label={t(field.label)}
              placeholder={field.placeholder}
              disabled={field.disabled}
              MenuProps={{
                anchorOrigin: {
                  vertical: "bottom",
                  horizontal: "left",
                },
                transformOrigin: {
                  vertical: "top",
                  horizontal: "left",
                },
                PaperProps: {
                  style: {
                    maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
                    overflow: "auto",
                  },
                },
              }}
            >
              {field.options?.map((option) => (
                <MenuItem key={option.code} value={option.code}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </>
        ),
        date: () => (
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label={`${t(field.label)}${field.required ? "*" : ""}`}
              value={field.value ? (dayjs.isDayjs(field.value) ? field.value : dayjs(field.value)) : null}
              onChange={(date) =>
                handleInputChange(
                  field.name,
                  date ? (dayjs.isDayjs(date) ? date : dayjs(date)) : null,
                  field.type,
                  field.disableFuture,
                  field?.disablePast
                )
              }
              minDate={dayjs(field.minDate)}
              disabled={field.disabled}
              disableFuture={field.disableFuture}
              disablePast={field.disablePast}
              slotProps={{
                textField: {
                  id: `Date-picker-${field.name}`,
                  inputProps: {
                    "data-test-id": `test-${field.name}`,
                    "aria-label": `${t(field.label)} Date Picker`,
                  },
                  variant: "outlined",
                },
              }}
              // slotProps={{
              //   textField: {
              //     variant: "outlined",
              //     InputProps: {
              //       endAdornment: (
              //         <InputAdornment position="end">
              //           <CalendarTodayOutlinedIcon />
              //         </InputAdornment>
              //       ),
              //     },
              //   },
              // }}
            />
          </LocalizationProvider>
        ),
        year: () => (
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label={`${t(field.label)}${field.required ? "*" : ""}`}
              views={["year"]}
              openTo="year"
              value={field.value ? (dayjs.isDayjs(field.value) ? field.value : dayjs(field.value)) : null}
              onChange={(date) =>
                handleInputChange(
                  field.name,
                  date ? (dayjs.isDayjs(date) ? date : dayjs(date)) : null,
                  field.type
                )
              }
              disabled={field.disabled}
              disableFuture
              slotProps={{
                textField: {
                  id: `Year-picker-${field.name}`,
                  inputProps: {
                    "data-test-id": `test-${field.name}`,
                    "aria-label": `${t(field.label)} Year Picker`,
                  },
                  variant: "outlined",
                },
              }}
            />
          </LocalizationProvider>
        ),
        time: () => {
          // Dynamically decide disableFuture based on paired date being today
          const pairedDateFieldName = field.name.endsWith("Time")
            ? field.name.replace(/Time$/, "Date")
            : undefined;
          const allFields = formSchema.sections?.flatMap((s) => s.fields) || [];
          const pairedDateField = pairedDateFieldName
            ? allFields.find(
                (f) => f.type === "date" && f.name === pairedDateFieldName
              )
            : undefined;
          const pairedDate = pairedDateField?.value
            ? dayjs(pairedDateField.value)
            : null;
          const shouldDisableFuture = Boolean(
            field.disableFuture && pairedDate && pairedDate.isSame(dayjs(), "day")
          );

          return (
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <TimePicker
                label={`${t(field.label)}${field.required ? "*" : ""}`}
                value={field.value ? (dayjs.isDayjs(field.value) ? field.value : dayjs(field.value)) : null}
                onChange={(time) =>
                  handleInputChange(
                    field.name,
                    time ? (dayjs.isDayjs(time) ? time : dayjs(time)) : null,
                    field.type,
                    field.disableFuture
                  )
                }
                disabled={field.disabled}
                disableFuture={shouldDisableFuture}
                disablePast={field.disablePast}
                slotProps={{
                  textField: {
                    id: `time-picker-${field.name}`,
                    inputProps: {
                      "data-test-id": `test-${field.name}`,
                      "aria-label": `${t(field.label)} Time Picker`,
                    },
                    variant: "outlined",
                  },
                }}
              />
            </LocalizationProvider>
          );
        },
        file: () => (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: "20px",
              width: isMobile ? 300 : 360,
            }}
          >
            <Button
              id={`${field.label}-upload-btn`}
              data-test-id={`test-${field.label}`}
              aria-label={`${field.label}`}
              component="label"
              role={undefined}
              variant="contained"
              tabIndex={-1}
              startIcon={<CloudUploadIcon />}
              sx={{ width: "max-content" }}
            >
              {t(field.label)}
              <VisuallyHiddenInput
                type="file"
                ref={(input) => (fileInputRefs.current[field.name] = input)}
                onChange={(e: ChangeEvent<HTMLInputElement>) =>
                  handleInputChange(field.name, e.target.files?.[0], field.type)
                }
              />
            </Button>
            <Box>
              {field.value && field.value?.name && (
                <Box sx={FileAClaimStyles.imageDisplayerBox}>
                  <Box sx={FileAClaimStyles.documentImage}>
                    <img src={document} alt="document-text" />
                  </Box>
                  <Box sx={FileAClaimStyles.fileNames}>
                    <Tooltip title={field.value?.name}>
                      <Typography sx={FileAClaimStyles.fileNameText}>
                        {field.value?.name}
                      </Typography>
                    </Tooltip>
                    <Box sx={FileAClaimStyles.fileSizeBox}>
                      <Typography sx={FileAClaimStyles.fileSizeText}>
                        {getFileSizeWithUnits(field.value?.size)}
                      </Typography>
                      <img src={dotImage} alt="dot" />
                      <Typography sx={FileAClaimStyles.completedText}>
                        {"Complete"}
                      </Typography>
                    </Box>
                  </Box>
                  <img
                    onClick={() => handleFileDelete(field.name)}
                    src={trashBin}
                    alt="trash-bin"
                  />
                  <img
                    src={tickMark}
                    alt="tickMark"
                    style={{ cursor: "default" }}
                  />
                </Box>
              )}
            </Box>
            {/* <TextField
              type="file"
              label={`${field.label}${field.required ? "*" : ""}`}
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                handleInputChange(field.name, e.target.files?.[0])
              }
              InputLabelProps={{ shrink: true }}
              disabled={field.disabled}
            /> */}
          </Box>
        ),
        toggle: () => (
          <FormControlLabel
            label={`${t(field.label)}${field.required ? "*" : ""}`}
            control={
              <Switch
                id={`${field.label}-Toggle`}
                data-test-id={`test-${field.label}`}
                aria-label={`${field.label}`}
                checked={Boolean(field.value)}
                onChange={(e: ChangeEvent<HTMLInputElement>) =>
                  handleInputChange(field.name, e.target.checked)
                }
                disabled={field.disabled}
              />
            }
          />
        ),
      };

      if (!fieldComponent[field.type]) {
        console.error(`Unsupported field type: ${field.type}`);
        return null;
      }

      return (
        <FormControl fullWidth key={field.name}>
          {fieldComponent[field.type]()}
          {field.error ? (
            <Typography sx={{ fontSize: "1rem", color: "red" }}>
              {t(field.error)}
            </Typography>
          ) : (
            <></>
          )}
        </FormControl>
      );
    },
    [handleInputChange, handleFileDelete, fetchValueSelectOptions]
  );

  const renderSection = useCallback(
    (section: ISection, index: number) => (
      <Box
        key={`section-${index}`}
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: "20px",
          marginBottom: "20px",
        }}
      >
        {section.sectionHeading && (
          <Typography variant="h5">{t(section.sectionHeading)}</Typography>
        )}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: `repeat(${section.layoutColumns}, 1fr)`,
            gap: "20px",
            "@media (max-width:600px)": {
              gridTemplateColumns: "1fr",
            },
          }}
        >
          {section.fields
            .filter((field) => !field.hidden)
            .map((field) => renderInputField(field))}
        </Box>
      </Box>
    ),
    [renderInputField]
  );
  const renderForm = useCallback(
    () =>
      formSchema.sections ? (
        <>
          {formSchema.sections.map((section, index) =>
            renderSection(section, index)
          )}
          <Box
            sx={{
              display: "flex",
              justifyContent: isFileAClaim === "FO" || isFileAClaim === "BOP" ? "space-between" : "flex-end",
            }}
          >
            {
              (isFileAClaim === "FO" || isFileAClaim === "BOP")  && (
                <Button
                  variant="contained"
                  color="primary"
                  //type="submit"
                  id="Save"
                  data-test-id="test-Save"
                  aria-label="Save"
                >
                  {t("Save")}
                </Button>
              )
            }
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              {rightActions ? rightActions : <></>}
              {isUpdate ? (
                <Button
                  variant="contained"
                  color="primary"
                  type="submit"
                  id="Save"
                  data-test-id="test-Save"
                  aria-label="Save"
                >
                  {t("Save")}
                </Button>
              ) : (
                <Button
                  variant="contained"
                  color="primary"
                  type="submit"
                  id="Submit"
                  data-test-id="test-Submit"
                  aria-label="Submit"
                >
                  {t("Submit")}
                </Button>
              )}
            </Box>
          </Box>
        </>
      ) : (
        <></>
      ),
    [formSchema, renderSection]
  );

  const validateFormValues = useCallback((): boolean => {
    let hasErrors = false;
    // Use current formSchema directly instead of updating state
    if (formSchema.sections) {
      formSchema.sections.forEach((section) =>
        section.fields.forEach((field) => {
           // Skip validation for hidden fields
           if (field.hidden) {
            field.error = "";
            return;
          }

          // Check for required field validation
          if (field.required && !field.value) {
            field.error = `${field.label} is required.`;
            hasErrors = true;
          }
          // Check for existing errors from real-time validation
          if (field.error) {
            hasErrors = true;
          }
          
          // Check for date validation
          if (field.type === "date" && field.value && !dayjs(field.value).isValid()) {
            field.error = `${field.label} has an invalid date.`;
            hasErrors = true;
          }
          
          // Check for time validation
          if (field.type === "time" && field.required && field.value) {
            if (!dayjs.isDayjs(field.value) ? !dayjs(field.value).isValid() : !field.value.isValid()) {
              field.error = `${field.label} has an invalid time.`;
              hasErrors = true;
            }
          }
          
          // Check for length constraints
          if (field.value && field.length && field.value.length > field.length) {
            field.error = `${t("Maximum length of")} ${field.length} ${t("characters exceeded")}`;
            hasErrors = true;
          }
          
          if (field.value && field.minLength && field.value.length < field.minLength) {
            field.error = `${t("Minimum length of")} ${field.minLength} ${t("characters required")}`;
            hasErrors = true;
          }
        })
      );
    }
    // Update the form schema to show errors
    setFormSchema((prevState) => {
      const newState = { ...prevState };
      if (newState.sections) {
        newState.sections.forEach((section) =>
          section.fields.forEach((field) => {
            // Find the corresponding field in the current schema
            const currentField = formSchema.sections?.flatMap(s => s.fields).find(f => f.name === field.name);
            if (currentField) {
              field.error = currentField.error;
            }
          })
        );
      }
      return newState;
    });
    return !hasErrors;
  }, [t, formSchema]);

  const handleFormSubmit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    
    const isValid = validateFormValues();
    
    if (isValid) {
      const formValues: { [key: string]: any } = {};
      formSchema.sections?.forEach((section) =>
        section.fields.forEach(
          (field) => (formValues[field.name] = field.value)
        )
      );
      onFormSubmit(formValues);
    } 
  };

  useEffect(() => {
    const fetchAsyncFields = async () => {
      const formSchema: IDynamicFormFieldsJson = JSON.parse(
        JSON.stringify(formFieldsJson)
      );
      const promises: Promise<void>[] =
        formSchema.sections?.flatMap((section) =>
          section.fields.map(async (field) => {
            // Map backend-provided `Length` to `length`
            if ((field as any).Length && !field.length) {
              (field as any).length = (field as any).Length;
            }

            if (field.asyncType === "asyncOptions" && asyncOptionsFunctions) {
              field.asyncOptionsFn =
                asyncOptionsFunctions[
                  field.asyncOptionsFn as keyof typeof field.asyncOptionsFn
                ];
              if (field.asyncOptionsFn && field.asyncEventType === "onLoad") {
                field.options = await field.asyncOptionsFn(field.name);
              }
            }
            if (field.asyncType === "asyncValue" && asyncValueFunctions) {
              field.asyncValueFn =
                asyncValueFunctions[
                  field.asyncValueFn as keyof typeof field.asyncValueFn
                ];
              if (field.asyncEventType === "onLoad") {
                field.value = await field.asyncValueFn();
              }
            }
            if (field.asyncType === "asyncLabel" && asyncLabelFunctions) {
              field.asyncLabelFn =
                asyncLabelFunctions[
                  field.asyncLabelFn as keyof typeof field.asyncLabelFn
                ];
              if (field.asyncEventType === "onLoad") {
                field.label = `${field.label} ${await field.asyncLabelFn()}`;
              }
            }
          })
        ) || [];

      await Promise.all(promises);

      setFormSchema(formSchema);
    };

    fetchAsyncFields();
  }, [
    formFieldsJson,
    asyncOptionsFunctions,
    asyncValueFunctions,
    asyncLabelFunctions,
  ]);

  return (
    <form onSubmit={handleFormSubmit}>
      {formSchema.formHeading ? (
        <Typography variant="h4" mb={4}>
          {formSchema.formHeading}
        </Typography>
      ) : (
        <></>
      )}
      {renderForm()}
    </form>
  );
};

export default DynamicForm;
